import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../styles/ExamHistory.css';
import { BsTrophy, BsInfoCircle, BsClockHistory, BsLayoutTextSidebarReverse, BsArrowRight } from 'react-icons/bs';

const ExamHistory = () => {
  const { examId } = useParams();
  const [examHistory, setExamHistory] = useState(null);
  const [error, setError] = useState(null);
  const [expandedAttemptId, setExpandedAttemptId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchExamHistory = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          throw new Error('Authentication token not found. Please log in.');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/exams/${examId}/my-attempts`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to fetch exam history');
        }

        const data = await response.json();
        setExamHistory(data);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchExamHistory();
  }, [examId]);

  if (error) {
    return <div className="exam-history-container"><div className="error-message">Error: {error}</div></div>;
  }

  if (!examHistory) {
    return <div className="exam-history-container"><p>Loading exam history...</p></div>;
  }

  const { exam, attempts, max_score_achieved, sections } = examHistory;

  // Pagination logic
  const totalPages = Math.ceil(attempts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedAttempts = attempts.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setExpandedAttemptId(null); // Collapse open rows when changing page
    }
  };

  return (
    <div className="exam-history-page">
      <header className="history-header">
        <h1>Lịch sử làm bài: <strong>{exam.title}</strong></h1>
        <div className="max-score-achieved">
          <BsTrophy />
          <span>Điểm cao nhất: <strong>{max_score_achieved}</strong></span>
        </div>
      </header>

      <div className="history-card">
        <h2 className="card-title"><BsInfoCircle /> Thông tin đề thi</h2>
        <div className="exam-sections-list">
          <div className="sections-header">
            <span>CÁC PHẦN THI</span>
            <span>TỔNG THỜI GIAN LÀM BÀI</span>
            <span>SỐ ĐIỂM TỐI ĐA</span>
          </div>
          {sections.map(section => (
            <div className="section-row" key={section.id}>
              <span className="section-name">{section.title}</span>
              <span className="section-duration">{section.duration_minutes} phút</span>
              <span className="section-max-score">{section.max_score} điểm</span>
            </div>
          ))}
        </div>
      </div>

      <div className="history-card">
        <h2 className="card-title"><BsClockHistory /> Các lần làm bài</h2>
        <div className="attempts-list">
          <div className="attempts-header">
            <span>Ngày làm bài</span>
            <span>Điểm số</span>
            <span>Trạng thái</span>
            <span>Hành động</span>
          </div>
          {displayedAttempts.map(attempt => (
            <React.Fragment key={attempt.id}>
              <div className="attempt-row" onClick={() => setExpandedAttemptId(expandedAttemptId === attempt.id ? null : attempt.id)}>
                <span>{new Date(attempt.started_at).toLocaleString()}</span>
                <span><strong className="score-highlight">{attempt.total_score} / {attempt.max_score}</strong></span>
                <span>
                  <span className={`status-dot ${attempt.completed_at ? 'completed' : 'incomplete'}`}></span>
                  {attempt.completed_at ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
                </span>
                <span className="action-cell">
                  <Link to={attempt.completed_at ? `/review/${attempt.id}` : `/attempt/${attempt.id}`} className="btn-review" onClick={(e) => e.stopPropagation()}>
                    {attempt.completed_at ? 'Xem lại' : <>Tiếp tục làm <BsArrowRight /></>}
                  </Link>
                </span>
              </div>
              {expandedAttemptId === attempt.id && (
                <div className="attempt-details">
                  <p className="details-title"><BsLayoutTextSidebarReverse /> Chi tiết các phần</p>
                  <div className="details-header">
                    <span>Phần thi</span>
                    <span>Điểm</span>
                    <span>Khiên</span>
                  </div>
                  {attempt.section_scores.map(sec => (
                    <div className="details-section-row" key={sec.section_id}>
                      <span>{sec.section_title}</span>
                      <span>{sec.score} / {sec.max_score}</span>
                      <span>{sec.number_shields}</span>
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="pagination-footer">
          <span>Showing {startIndex + 1} to {Math.min(endIndex, attempts.length)} of {attempts.length} entries</span>
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&lt;</button>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>&gt;</button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ExamHistory;